import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  LeadBoardResponse,
  LeadCatalogResponse,
  LeadDetailResponse,
  LeadMetricsResponse,
  LeadMyDayResponse,
  LeadPhoneCheckResponse,
  LeadSellersResponse,
  LeadStagesResponse,
  QuickCreateLeadResponse,
  RepurchaseCandidatesResponse,
  RepurchaseRunResponse,
} from '#interfaces/http-response/responses/http-responses';
import {
  CreateLeadProject,
  LeadCatalogItem,
  LeadFollowUp,
  ScheduleLeadVisit,
  LeadSeller,
  LeadType,
  MoveLeadStage,
  QuickCreateLead,
  UpdateLead,
} from '#interfaces/lead/lead.interface';
import { cleanObjectStringValues } from '#root/http/utils';
import { HeadersHttpService } from '#utils/http-headers/http-headers.service';
import { environment } from 'environments/environment';
import { Observable, map, shareReplay } from 'rxjs';

/**
 * Servicio del CRM de leads (controlador `Lead` del API v1).
 * Los catálogos (motivos de pérdida, orígenes y vendedores) se piden una vez por sesión.
 */
@Injectable({
  providedIn: 'root'
})
export class LeadService {

  private readonly _baseUrl: string = environment.baseUrl + 'Lead/';

  private _lossReasons$?: Observable<LeadCatalogItem[]>;
  private _origins$?: Observable<LeadCatalogItem[]>;
  private _sellers$?: Observable<LeadSeller[]>;

  constructor(private _httpClient: HttpClient, private _httpHeaders: HeadersHttpService) { }

  getMyDay(team: boolean = false): Observable<LeadMyDayResponse> {
    return this._httpClient.get<LeadMyDayResponse>(this._baseUrl + 'MyDay', {
      headers: this._httpHeaders.getHeadersPrivate(),
      params: { team },
    });
  }

  getMetrics(from: string, to: string): Observable<LeadMetricsResponse> {
    return this._httpClient.get<LeadMetricsResponse>(this._baseUrl + 'Metrics', {
      headers: this._httpHeaders.getHeadersPrivate(),
      params: { from, to },
    });
  }

  /** Clientes a los que les toca recomprar o reactivar (solo gerencia). */
  getRepurchaseCandidates(): Observable<RepurchaseCandidatesResponse> {
    return this._httpClient.get<RepurchaseCandidatesResponse>(this._baseUrl + 'Repurchase', {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  /** Crea ahora los leads de recompra pendientes (el API lo hace solo cada mañana). */
  runRepurchase(): Observable<RepurchaseRunResponse> {
    return this._httpClient.post<RepurchaseRunResponse>(this._baseUrl + 'Repurchase/Run', {}, {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  getBoard(leadType: LeadType, userId: string | null, search: string = ''): Observable<LeadBoardResponse> {
    const params: Record<string, string | number> = { leadType };
    if (userId) params['userId'] = userId;
    if (search?.trim()) params['search'] = search.trim();
    return this._httpClient.get<LeadBoardResponse>(this._baseUrl + 'Board', {
      headers: this._httpHeaders.getHeadersPrivate(),
      params,
    });
  }

  getStages(leadType?: LeadType): Observable<LeadStagesResponse> {
    return this._httpClient.get<LeadStagesResponse>(this._baseUrl + 'Stages', {
      headers: this._httpHeaders.getHeadersPrivate(),
      params: leadType ? { leadType } : {},
    });
  }

  getById(id: number): Observable<LeadDetailResponse> {
    return this._httpClient.get<LeadDetailResponse>(this._baseUrl + 'GetById/' + id, {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  checkPhone(phone: string): Observable<LeadPhoneCheckResponse> {
    return this._httpClient.get<LeadPhoneCheckResponse>(this._baseUrl + 'CheckPhone', {
      headers: this._httpHeaders.getHeadersPrivate(),
      params: { phone },
    });
  }

  quickCreate(lead: QuickCreateLead): Observable<QuickCreateLeadResponse> {
    return this._httpClient.post<QuickCreateLeadResponse>(this._baseUrl + 'QuickCreate', cleanObjectStringValues(lead), {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  update(lead: UpdateLead): Observable<LeadDetailResponse> {
    return this._httpClient.put<LeadDetailResponse>(this._baseUrl + 'Update/' + lead.id, cleanObjectStringValues(lead), {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  moveStage(command: MoveLeadStage): Observable<LeadDetailResponse> {
    return this._httpClient.put<LeadDetailResponse>(this._baseUrl + 'MoveStage/' + command.id, cleanObjectStringValues(command), {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  followUp(command: LeadFollowUp): Observable<LeadDetailResponse> {
    return this._httpClient.post<LeadDetailResponse>(this._baseUrl + 'FollowUp/' + command.id, cleanObjectStringValues(command), {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  reassign(id: number, userId: string): Observable<LeadDetailResponse> {
    return this._httpClient.put<LeadDetailResponse>(this._baseUrl + 'Reassign/' + id, { id, userId }, {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  scheduleVisit(command: ScheduleLeadVisit): Observable<LeadDetailResponse> {
    return this._httpClient.post<LeadDetailResponse>(this._baseUrl + 'ScheduleVisit/' + command.id, cleanObjectStringValues(command), {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  saveSurvey(id: number, surveyData: string, completed: boolean): Observable<LeadDetailResponse> {
    return this._httpClient.put<LeadDetailResponse>(this._baseUrl + 'Survey/' + id, { id, surveyData, completed }, {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  addDocument(id: number, name: string, url: string, documentTypeName: string): Observable<LeadDetailResponse> {
    return this._httpClient.post<LeadDetailResponse>(this._baseUrl + 'Documents/' + id, { id, name, url, documentTypeName }, {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  deleteDocument(id: number, documentId: number): Observable<LeadDetailResponse> {
    return this._httpClient.delete<LeadDetailResponse>(this._baseUrl + 'Documents/' + id + '/' + documentId, {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  createProject(command: CreateLeadProject): Observable<LeadDetailResponse> {
    return this._httpClient.post<LeadDetailResponse>(this._baseUrl + 'CreateProject/' + command.id, cleanObjectStringValues(command), {
      headers: this._httpHeaders.getHeadersPrivate(),
    });
  }

  /** Vendedores activos (para reasignar). */
  getSellers(): Observable<LeadSeller[]> {
    this._sellers$ ??= this._httpClient
      .get<LeadSellersResponse>(this._baseUrl + 'Sellers', { headers: this._httpHeaders.getHeadersPrivate() })
      .pipe(map(res => res.data ?? []), shareReplay(1));
    return this._sellers$;
  }

  /** Motivos de pérdida activos. */
  getLossReasons(): Observable<LeadCatalogItem[]> {
    this._lossReasons$ ??= this._getCatalog('LossReason/GetAll');
    return this._lossReasons$;
  }

  /** Orígenes de lead activos. */
  getOrigins(): Observable<LeadCatalogItem[]> {
    this._origins$ ??= this._getCatalog('TypeOrigin/GetAll');
    return this._origins$;
  }

  private _getCatalog(path: string): Observable<LeadCatalogItem[]> {
    return this._httpClient
      .get<LeadCatalogResponse>(environment.baseUrl + path, {
        headers: this._httpHeaders.getHeadersPrivate(),
        params: { PageNumber: 1, PageSize: 100, All: true },
      })
      .pipe(
        map(res => (res.data ?? []).filter(item => item.isActive).sort((a, b) => a.name.localeCompare(b.name, 'es'))),
        shareReplay(1),
      );
  }
}
